import { useState } from "react";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaMapMarkerAlt,
  FaRupeeSign,
  FaRoad,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";
import axios from "axios";
import { getDriverAuthHeaders } from "../../utils/driverAuthUtils";
import { API_URL } from "../../config/apiConfig";

const RideRequestModal = ({ isOpen, onClose, rideData, onAccept }) => {
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const handleAcceptRide = async () => {
    if (!rideData?.bookingId) {
      toast.error("Booking ID is missing");
      return;
    }

    setIsAccepting(true);
    try {
      const headers = getDriverAuthHeaders();
      const response = await axios.post(
        `${API_URL}/booking/${rideData.bookingId}/accept-ride`,
        {},
        { headers }
      );

      if (response.data.success) {
        toast.success("Ride accepted! Heading to passenger...");
        if (onAccept) {
          onAccept(response.data.data);
        }
        onClose();
      } else {
        toast.error(response.data.message || "Failed to accept ride");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to accept ride";
      toast.error(errorMsg);
      console.error("Error accepting ride:", error);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDeclineRide = () => {
    setIsDeclining(true);
    // Just close the modal for now - decline functionality can be added later
    toast.info("Ride declined");
    onClose();
    setIsDeclining(false);
  };

  if (!isOpen || !rideData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
      <div className="w-full bg-white rounded-t-lg shadow-xl animate-slide-up max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 text-white p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">New Ride Available!</h2>
            <p className="text-sm text-green-100">Accept within 30 seconds</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-green-700 rounded-full transition"
            disabled={isAccepting || isDeclining}
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Pickup Location */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FaMapMarkerAlt className="text-red-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 font-medium">Pickup</p>
                <p className="text-lg font-semibold text-gray-800">
                  {rideData.pickup?.address || "Location not specified"}
                </p>
              </div>
            </div>
          </div>

          {/* Drop Location */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FaMapMarkerAlt className="text-green-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 font-medium">Drop</p>
                <p className="text-lg font-semibold text-gray-800">
                  {rideData.drop?.address || "Location not specified"}
                </p>
              </div>
            </div>
          </div>

          {/* Schedule & Return Date/Time if Round Trip */}
          {(rideData.returnDate || rideData.pickupDate || rideData.pickupTime || rideData.tripType === "roundTrip") && (
            <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-3.5 space-y-1.5">
              {(rideData.pickupDate || rideData.pickupTime) && (
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <FaCalendarAlt className="text-slate-400 text-xs" />
                  <span className="font-medium text-slate-500">Pickup:</span>
                  <span className="font-bold text-slate-800">
                    {rideData.pickupDate ? new Date(rideData.pickupDate).toLocaleDateString() : ""} {rideData.pickupTime ? `at ${rideData.pickupTime}` : ""}
                  </span>
                </div>
              )}
              {(rideData.returnDate || rideData.tripType === "roundTrip") && rideData.returnDate && (
                <div className="flex items-center gap-2 text-xs text-indigo-800">
                  <FaCalendarAlt className="text-indigo-500 text-xs" />
                  <span className="font-bold uppercase tracking-wider text-[10px] bg-indigo-200 text-indigo-800 px-1.5 py-0.5 rounded">Round Trip</span>
                  <span className="font-black text-indigo-900">
                    Return: {new Date(rideData.returnDate).toLocaleDateString()} {rideData.returnTime ? `at ${rideData.returnTime}` : ""}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Ride Details Grid */}
          <div className="grid grid-cols-3 gap-3">
            {/* Distance */}
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <FaRoad className="text-blue-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Distance</p>
              <p className="text-lg font-bold text-gray-800">
                {rideData.distance ? `${rideData.distance} km` : "N/A"}
              </p>
            </div>

            {/* Duration */}
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <FaClock className="text-purple-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Duration</p>
              <p className="text-lg font-bold text-gray-800">
                {rideData.duration
                  ? `${Math.round(rideData.duration / 60)} min`
                  : "N/A"}
              </p>
            </div>

            {/* Fare */}
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <FaRupeeSign className="text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Fare</p>
              <p className="text-lg font-bold text-gray-800">
                ₹{rideData.fare ? Math.round(rideData.fare) : "N/A"}
              </p>
            </div>
          </div>

          {/* Passenger Info */}
          {rideData.passenger && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <p className="text-sm text-gray-600 font-medium mb-2">Passenger</p>
              <div>
                <p className="font-semibold text-gray-800">
                  {rideData.passenger.name || "Guest"}
                </p>
                {rideData.passenger.phone && (
                  <p className="text-sm text-gray-600">
                    {rideData.passenger.phone}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Cab Type */}
          {rideData.cabType && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 font-medium mb-1">Cab Type</p>
              <p className="font-semibold text-gray-800">
                {typeof rideData.cabType === 'object'
                  ? (rideData.cabType.name || rideData.cabType._id || 'Unknown')
                  : rideData.cabType}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-3">
          <button
            onClick={handleAcceptRide}
            disabled={isAccepting || isDeclining}
            className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-bold py-3 rounded-lg transition flex items-center justify-center space-x-2"
          >
            {isAccepting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Accepting...</span>
              </>
            ) : (
              <>
                <span>✓</span>
                <span>Accept Ride</span>
              </>
            )}
          </button>

          <button
            onClick={handleDeclineRide}
            disabled={isAccepting || isDeclining}
            className="w-full bg-gray-300 hover:bg-gray-400 disabled:bg-gray-400 text-gray-800 font-bold py-3 rounded-lg transition"
          >
            {isDeclining ? "Declining..." : "Decline"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RideRequestModal;
